import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppleHeader from '../components/AppleHeader';

export const ConfigurarAnuncio: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map((id) => Number(id)).filter((n) => Number.isFinite(n)),
    [idsParam]
  );
  const modo = (params.get('modo') as 'rapido' | 'personalizado') || 'rapido';
  // Composición (modelo/producto) with backward compatibility when it was sent as 'tipo'
  const composicionRaw = (params.get('composicion') || params.get('tipo')) as string | null;
  const composicionParam: 'modelo' | 'producto' | null = composicionRaw === 'modelo' || composicionRaw === 'producto' ? (composicionRaw as any) : null;
  const composicionLabel = composicionParam === 'modelo'
    ? 'Modelo con producto'
    : composicionParam === 'producto'
      ? 'Solo producto'
      : null;

  // Content type (imagen/video)
  const contentTypeRaw = params.get('tipo') as 'imagen' | 'video' | null;
  const contentTypeLabel = contentTypeRaw === 'imagen' ? 'Imágenes' : contentTypeRaw === 'video' ? 'Video' : null;

  // Optional model preferences (from personalized flow when composicion = 'modelo')
  const modeloMode = params.get('modelo_mode') as 'ia' | 'guardados' | 'personalizado' | null;
  const modeloGuardadoId = params.get('modelo_guardado_id') || '';
  const modeloGuardadoNombre = params.get('modelo_guardado_nombre') || '';
  const pose = params.get('pose') || '';
  const genero = params.get('genero') || '';
  const edad = params.get('edad') || '';
  const tipoCuerpo = params.get('tipo_cuerpo') || '';
  const tonoPiel = params.get('tono_piel') || '';
  const colorPelo = params.get('color_pelo') || '';
  const largoPelo = params.get('largo_pelo') || '';
  const descModelo = params.get('desc_modelo') || '';
  const poseLabelMap: Record<string, string> = {
    'cuerpo-entero': 'Plano completo',
    'plano-superior': 'Plano superior',
    'plano-inferior': 'Plano inferior',
    'plano-espalda': 'Plano espalda',
    'plano-sentada': 'Plano sentada',
    'solo-producto': 'Solo producto',
    'plano-enfoque': 'Plano enfoque',
    'foco-torso': 'Plano enfoque',
    'foco-pantalon': 'Plano enfoque',
    'foco-zapatos': 'Plano enfoque',
    'foco-mano': 'Plano enfoque',
    'foco-cabeza': 'Plano enfoque',
  };
  const poseLabel = pose ? (poseLabelMap[pose] || pose) : '';
  const modeloSourceLabel = modeloMode === 'guardados'
    ? (modeloGuardadoNombre ? `Modelo guardado: ${modeloGuardadoNombre}` : 'Modelo guardado')
    : modeloMode === 'personalizado'
      ? 'Modelo personalizado'
      : modeloMode === 'ia'
        ? 'Sugerido por IA'
        : '';

  // Background selection (from ElegirFondoAnuncio)
  const backgroundMode = params.get('background_mode') as 'ia' | 'blanco' | 'elegido' | null;
  const backgroundUrl = params.get('background_url') || '';
  const backgroundLabel = backgroundMode === 'ia'
    ? 'Fondo sugerido por IA'
    : backgroundMode === 'blanco'
      ? 'Fondo blanco'
      : backgroundMode === 'elegido'
        ? 'Fondo seleccionado'
        : '';

  // Composition selection (from SeleccionarComposicionAnuncio)
  const compositionMode = params.get('composition_mode') as 'ninguno' | 'texto-simple' | 'elegido' | null;
  const compositionUrl = params.get('composition_url') || '';
  const compositionText = params.get('composition_text') || '';
  const compositionLabel = compositionMode === 'ninguno'
    ? 'Ninguno'
    : compositionMode === 'texto-simple'
      ? 'Texto simple'
      : compositionMode === 'elegido'
        ? 'Composición seleccionada'
        : '';

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto min-h-[100dvh] md:min-h-screen">
      <div className="bg-white rounded-md shadow-sm p-4 sm:p-8">
        <div className="mb-6">
          <AppleHeader title="Configurar anuncio" />
        </div>

        {composicionParam === 'modelo' && (
          <div className="mt-2 border border-gray-200 rounded-md p-4">
            <h2 className="text-sm font-semibold text-gray-900">Preferencias del modelo</h2>
            <ul className="mt-2 text-sm text-gray-700 space-y-1">
              {modeloSourceLabel && (
                <li><span className="text-gray-500">Modelo:</span> {modeloSourceLabel}{modeloGuardadoId && !modeloGuardadoNombre ? ` (#${modeloGuardadoId})` : ''}</li>
              )}
              {poseLabel && (
                <li><span className="text-gray-500">Pose:</span> {poseLabel}</li>
              )}
              {genero && (
                <li><span className="text-gray-500">Género:</span> {genero}</li>
              )}
              {edad && (
                <li><span className="text-gray-500">Edad:</span> {edad}</li>
              )}
              {tipoCuerpo && (
                <li><span className="text-gray-500">Tipo de cuerpo:</span> {tipoCuerpo}</li>
              )}
              {tonoPiel && (
                <li><span className="text-gray-500">Tono de piel:</span> {tonoPiel}</li>
              )}
              {colorPelo && (
                <li><span className="text-gray-500">Color de pelo:</span> {colorPelo}</li>
              )}
              {largoPelo && (
                <li><span className="text-gray-500">Largo de pelo:</span> {largoPelo}</li>
              )}
              {!poseLabel && !genero && !edad && !tipoCuerpo && !tonoPiel && !colorPelo && !largoPelo && !descModelo && (
                <li className="text-gray-500">Sin preferencias seleccionadas</li>
              )}
            </ul>
            {descModelo && (
              <p className="text-sm text-gray-700 mt-2"><span className="text-gray-500">Descripción:</span> {descModelo}</p>
            )}
          </div>
        )}

        {/* Fondo */}
        {backgroundMode && (
          <div className="mt-4 border border-gray-200 rounded-md p-4">
            <h2 className="text-sm font-semibold text-gray-900">Fondo</h2>
            <div className="mt-2 text-sm text-gray-700">
              <p><span className="text-gray-500">Selección:</span> {backgroundLabel}</p>
              {backgroundMode === 'elegido' && backgroundUrl && (
                <div className="mt-2">
                  <div className="w-full sm:w-64 aspect-video rounded-md overflow-hidden border border-gray-200 bg-gray-100">
                    <img src={backgroundUrl} alt="Fondo elegido" className="w-full h-full object-cover" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Composición */}
        {compositionMode && (
          <div className="mt-4 border border-gray-200 rounded-md p-4">
            <h2 className="text-sm font-semibold text-gray-900">Composición</h2>
            <div className="mt-2 text-sm text-gray-700">
              <p><span className="text-gray-500">Selección:</span> {compositionLabel}</p>
              {compositionMode === 'texto-simple' && compositionText && (
                <p className="text-sm text-gray-700 mt-2"><span className="text-gray-500">Texto:</span> {compositionText}</p>
              )}
              {compositionMode === 'elegido' && compositionUrl && (
                <div className="mt-2">
                  <div className="w-full sm:w-64 aspect-video rounded-md overflow-hidden border border-gray-200 bg-gray-100">
                    <img src={compositionUrl} alt="Composición elegida" className="w-full h-full object-cover" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="rounded-md border border-dashed border-gray-200 p-6 text-gray-600">
          Próximamente: formulario para configurar el anuncio (copys, formatos, plataformas, etc.).
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(`/redes-sociales/crear-anuncio/seleccionar-productos?modo=${modo}`)}
            className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Volver a seleccionar
          </button>

          <button
            type="button"
            className="px-4 py-2 bg-blue-500 text-white rounded-md shadow-sm hover:bg-blue-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => {
              // TODO: siguiente paso (por definir)
            }}
          >
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigurarAnuncio;
